<script setup lang="ts">
import { computed, ref } from "vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import FloatLabel from "primevue/floatlabel";
import Message from "primevue/message";
import { useAuthModal } from "@/features/auth/store";
import { useAuthStore } from "@/features/auth/store";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const email = ref("");
const emailError = ref("");
const loadingState = ref(false);

const disableSubmit = computed(() => {
  return email.value === "" || !!emailError.value.length;
});

function signInHandler() {
  useAuthModal().showAuthModal("signIn");
}

const validateEmail = () => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email.value)) {
    emailError.value = t("signup.email_error");
  } else {
    emailError.value = "";
  }
};

async function forgotPasswordHandler() {
  if (email.value && !emailError.value) {
    loadingState.value = true;
    try {
      await useAuthStore().forgotPassword(email.value);
    } finally {
      loadingState.value = false;
    }
  }
}
</script>

<template>
  <div class="reset-password__content">
    <span class="reset-password__title h2">{{ t("reset_password.forgot_title") }}</span>
    <div class="reset-password__content__wrapper">
      <div class="reset-password__inputs">
        <div class="reset-password__text">
          {{ t("reset_password.instruction") }}
        </div>
        <div>
          <FloatLabel>
            <InputText
              v-model="email"
              :invalid="!!emailError"
              id="email"
              @input="validateEmail"
              @blur="validateEmail"
              name="email"
              autocomplete="email"
              class="email"
              :pt="{
                root: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                  },
                },
                input: {
                  autocomplete: 'email',
                  'data-form-type': 'email',
                },
              }"
            />
            <label for="email">{{ t("signup.email_label") }}</label>
          </FloatLabel>
          <Message
            v-if="!!emailError"
            severity="error"
            size="small"
            :pt="{
              root: {
                style: {
                  outline: 'none',
                },
              },
              content: {
                style: {
                  padding: '0',
                  background: 'transparent',
                },
              },
              text: {
                style: {
                  paddingLeft: '16px',
                  fontSize: '12px',
                },
              },
            }"
            >{{ emailError }}
          </Message>
        </div>
      </div>
    </div>
    <div class="reset-password__submit_btn__wrapper">
      <Button
        class="reset-password-submit"
        :label="t('reset_password.submit_button')"
        :disabled="disableSubmit"
        :loading="loadingState"
        @click="forgotPasswordHandler"
      />
    </div>
    <div class="old__user">
      <span>{{ t("reset_password.back_to") }}</span>
      <Button
        class="sign-in_link"
        :label="t('reset_password.sign_in')"
        text
        @click="signInHandler"
      />
    </div>
  </div>
</template>
<style scoped>
.reset-password__content {
  display: flex;
  border-radius: 40px;
  padding: 0 40px 40px 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  /* Figma Authorization flow dialogs = 480 */
  width: min(90vw, 480px);
  max-width: 480px;
  box-sizing: border-box;
  gap: 20px;
}

.reset-password__title {
  text-align: center;
}

.reset-password__content__wrapper {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.reset-password_third_party button {
  display: flex;
  border-radius: 8px;
  gap: 8px;
  padding: 12px 20px;
}

.reset-password__inputs {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.reset-password__inputs__wrapper {
  width: 100%;
}

.email,
.password {
  width: 100%;
}

.reset-password__text {
  display: flex;
  text-align: center;
}

.reset-password__submit_btn__wrapper {
  display: flex;
  width: 100%;
}

.reset-password-submit {
  width: 100%;
  border-radius: 100px;
}

.old__user {
  display: flex;
  align-items: center;
}
</style>
